import { Box, Button, Heading, HideWithCSS, Icon, LinkOverlay, VStack } from "@biblioteksentralen/react";
import { getPath, ResolvedSommerlesBannerData } from "@libry-content/common";
import { useTranslation } from "../../utils/hooks/useTranslation";
import InternalLinkOverlay from "../InternalLinkOverlay";
import { Countdown } from "./CountDown";
import { ExternalLink } from "react-feather";
import { SanityImagePreservingAspectRatio } from "../images/SanityImagePreservingAspectRatio";
import Character from "./Character";
import { SanityImageFillAvailableSpace } from "../images/SanityImageFillAvailableSpace";
import { useState } from "react";

type BannerContentPreludeProps = {
  sommerles: ResolvedSommerlesBannerData;
  smallScreen: boolean;
  onLandingPage: boolean;
};

export const BannerContentPrelude = (props: BannerContentPreludeProps) => {
  const { t } = useTranslation();
  const sommerles = props.sommerles;
  if (!sommerles?.templateContent) return null;

  return (
    <>
      <PreludeAssetLayout {...props} />
      <VStack
        zIndex={2}
        justifyContent={{ base: "space-around", md: "space-between" }}
        width="100%"
        height="100%"
        padding={{ base: "1rem 0", md: "2rem 0" }}
        spacing="1rem"
        position="relative"
      >
        <Heading
          as="h2"
          display="flex"
          flexDirection="column"
          alignItems="center"
          color="#F3BA6A"
          sx={{ WebkitTextStroke: "3px #471230", paintOrder: "stroke fill" }}
          textShadow={`5px 5px .5em #471230`}
          fontSize={{ base: ".8rem", md: "1.2rem" }}
          lineHeight={1}
        >
          <Box fontSize="1.25em">{t("Vi teller ned til")}</Box>
          <Box fontSize="2.5em">{t("Sommerles")}</Box>
        </Heading>
        <Countdown sommerles={sommerles} smallScreen={props.smallScreen} />
        <Button
          as={sommerles.separatePage ? InternalLinkOverlay : LinkOverlay}
          href={sommerles?.separatePage ? getPath(sommerles) : sommerles?.templateContent?.url!}
          background="#F3BA6A"
          fontSize="xl"
          color=" #471230"
          size={{ base: "md", md: "lg" }}
          outline="3px solid #471230"
          rightIcon={sommerles?.separatePage ? undefined : <Icon as={ExternalLink} size="1em" />}
        >
          {t("Les om")} {t("Sommerles")}
        </Button>
      </VStack>
    </>
  );
};

const PreludeAssetLayout = (props: BannerContentPreludeProps) => {
  const sommerles = props.sommerles;
  const [backgroundHasLoaded, setBackgroundHasLoaded] = useState<boolean>(false);
  if (!sommerles?.templateContent || !sommerles.startDate || !sommerles.endDate) return null;
  const character1Image = sommerles.templateContent.character1Image;
  const character2Image = sommerles.templateContent.character2Image;
  const detail = sommerles.templateContent.detail1;
  const backgroundImage = sommerles.templateContent.countDownBackgroundImage;

  return (
    <Box
      position="absolute"
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="space-between"
      borderRadius={{ base: 0, md: "0.5rem" }}
      overflow={{ base: "hidden", md: "visible" }}
      isolation="isolate"
    >
      <Box position="relative" width="100%" display="flex" alignItems="center" justifyContent="center">
        {backgroundHasLoaded && (
          <HideWithCSS below="md">
            <Box width="24%" left="79%" transform="translate(0, -7%)" position="absolute" zIndex={10}>
              <Character image={character1Image} position="relative" />
            </Box>
          </HideWithCSS>
        )}
      </Box>
      <Box
        position="relative"
        width="100%"
        height="100%"
        borderRadius={{ base: 0, md: "0.5rem" }}
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box position="absolute" width="100%" height="100%">
          <SanityImageFillAvailableSpace
            image={backgroundImage}
            resolution={1000}
            quality={100}
            _groupHover={{}}
            imageWrapperProps={{ borderRadius: 0 }}
            customNextImageProps={{
              onLoad: () => setBackgroundHasLoaded(true),
            }}
          />
        </Box>
        {backgroundHasLoaded && (
          <>
            <SanityImagePreservingAspectRatio
              image={detail}
              resolution={1000}
              position="absolute"
              width="115%"
              boxShadow="none"
            />
            <SanityImagePreservingAspectRatio
              image={detail}
              resolution={1000}
              position="absolute"
              width="112%"
              transform="rotate(0.5turn)"
              boxShadow="none"
            />
            <SanityImagePreservingAspectRatio
              image={detail}
              resolution={1000}
              position="absolute"
              width="115%"
              transform="scale(-1, 1)"
              boxShadow="none"
            />
            <SanityImagePreservingAspectRatio
              image={detail}
              resolution={1000}
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="112%"
              transform="translate(0, -10%) scale(-1, 1) rotate(0.5turn)"
              boxShadow="none"
              zIndex={{ base: 11, md: 9 }}
              opacity={{ base: "85%", md: "100%" }}
            />
          </>
        )}
      </Box>
      <Box position="relative" width="100%" display="flex" alignItems="center" justifyContent="center">
        {backgroundHasLoaded && (
          <Box
            zIndex={10}
            width={{ base: "57%", sm: "45%", md: "35%" }}
            left={{ base: "-3.5%", sm: "-2%", md: "-2.5%" }}
            transform={{ base: "translate(0, -27%)", md: "translate(0, -45%)" }}
            position="absolute"
          >
            <Character image={character2Image} position="relative" />
          </Box>
        )}
      </Box>
    </Box>
  );
};
